import { action, computed, makeObservable, observable } from "mobx";
import { trackEvent } from "../../core/trackEvent";
import type { AffiliatesHotelsCache } from "../../plans/PlanModel";
import { log } from "../../ui/log";
import { getThisWeekendDates } from "../utils";
import { fetchHotelsApi, type SearchHotelsParams } from "./apis";
import type { HotelRawData } from "./types-hotel";

const getSearchParamsDefaults = (
  p: Partial<SearchHotelsParams> | null,
): SearchHotelsParams => {
  const { checkin_date, checkout_date } = getThisWeekendDates();

  return {
    adults_number: 2, // always 2
    checkin_date,
    checkout_date,
    latitude: 0,
    longitude: 0,
    ...p,
  };
};

function getHashFromSearchParams(p: SearchHotelsParams): string {
  return btoa(JSON.stringify(p));
}

const baseUrl =
  "https://www.awin1.com/awclick.php?awinmid=6776&awinaffid=1653359&clickref=tips-bar&ued=";
export class HotelsApiManager {
  @observable isLoading = false;

  @observable private searchParams: SearchHotelsParams | null = null;

  @observable private hotelsData: HotelRawData[] = [];

  @observable private showAmount = 20;

  constructor(
    private onSaveCache: (cache: AffiliatesHotelsCache) => void,
    private cache?: AffiliatesHotelsCache,
  ) {
    makeObservable(this);
  }

  getAffiliateUrl(hotelUrl: string) {
    if (this.searchParams) {
      // Append checkindates and adults
      const [checkinYear, checkinMonth, checkinDay] =
        this.searchParams.checkin_date.split("-").map(Number);
      const [checkoutYear, checkoutMonth, checkoutDay] =
        this.searchParams.checkout_date.split("-").map(Number);

      // Construct the Booking.com query parameters
      const bookingQueryParams = new URLSearchParams({
        checkin_year: checkinYear.toString(),
        checkin_month: checkinMonth.toString(),
        checkin_monthday: checkinDay.toString(),
        checkout_year: checkoutYear.toString(),
        checkout_month: checkoutMonth.toString(),
        checkout_monthday: checkoutDay.toString(),
        group_adults: this.searchParams.adults_number.toString(),
        group_children: "0",
        no_rooms: "1",
      }).toString();

      const bookingUrl = `${hotelUrl}?${bookingQueryParams}`;
      const encodedUrl = encodeURIComponent(bookingUrl);

      return `${baseUrl}${encodedUrl}`;
    }

    return `${baseUrl}${hotelUrl}`;
  }

  isValidForNewSearch(): boolean {
    // Check if checkin_date is a valid date in the future if set
    if (this.searchParams?.checkin_date) {
      const checkInDate = new Date(this.searchParams?.checkin_date);
      const checkInDateInFuture =
        checkInDate instanceof Date &&
        !Number.isNaN(checkInDate.getTime()) &&
        checkInDate > new Date();
      if (!checkInDateInFuture) {
        return false;
      }
    }

    return (
      this.searchParams?.latitude !== 0 && this.searchParams?.longitude !== 0
    );
  }

  @action updateParams(p: Partial<SearchHotelsParams> | null) {
    this.searchParams = this.searchParams
      ? { ...this.searchParams, ...p }
      : getSearchParamsDefaults(p);

    // If no data cached or this is a new hash, fetch data
    const newHash = getHashFromSearchParams(this.searchParams);

    const cachedData = this.cache;

    if (cachedData?.hash === newHash) {
      log.dev("Found cached affilate data");
      this.hotelsData = cachedData.data;
    } else {
      if (this.isValidForNewSearch()) {
        this.fetchData();
      } else {
        log.dev("Skipped fetching new data...");
        trackEvent("affiliate_hotels_search_skipped");
      }
    }
  }

  @action async fetchData() {
    if (!this.searchParams) {
      return;
    }

    if (this.isLoading) {
      return;
    }

    // Fetch data based on current setup
    this.isLoading = true;

    this.hotelsData = await fetchHotelsApi(this.searchParams);

    this.isLoading = false;

    if (this.hotelsData.length) {
      this.cache = {
        createdAt: Date.now(),
        hash: getHashFromSearchParams(this.searchParams),
        data: this.hotelsData,
      };

      this.onSaveCache(this.cache);

      trackEvent("affiliate_hotels_searched");
    }
  }

  @action showMore() {
    this.showAmount += 10;
  }

  @computed get hotels(): HotelRawData[] {
    return this.hotelsData.slice(0, this.showAmount);
  }

  @computed get daysCount(): number {
    if (this.searchParams?.checkin_date && this.searchParams?.checkout_date) {
      const checkinDate = new Date(this.searchParams.checkin_date);
      const checkoutDate = new Date(this.searchParams.checkout_date);

      const timeDifference = checkoutDate.getTime() - checkinDate.getTime();
      const dayDifference = timeDifference / (1000 * 3600 * 24);

      // Use Math.floor to get the number of full nights
      return Math.floor(dayDifference);
    }
    return 0;
  }
}
